import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';

import { JsonHeader } from '../shared/utilities/constants';

import { map } from 'rxjs/operators';
import { Transaction } from './models/transaction';
import { StripeSubscription } from '../shared/models/stripe-subscription';
import { StripeCustomer, StripeTransaction } from '../product/models/models';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable()
export class PaymentService {
  stripePublicApiKey$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private _keyFetchStarted = false;
  private readonly _document = inject(DOCUMENT);

  constructor(private _http: HttpClient) {}

  /**
   * Lazily fetches the Stripe public API key once per service instance.
   * Safe to call multiple times — subsequent calls are no-ops.
   */
  ensureApiKeyLoaded(): void {
    if (this._keyFetchStarted) {
      return;
    }
    this._keyFetchStarted = true;
    this.fetchStripePublicApiKey().subscribe(apiKey => {
      this.stripePublicApiKey$.next(apiKey);
    });
  }

  /**
   * Dynamically loads js.stripe.com/v3 (once) and emits when ready.
   * Call this from components that mount Stripe Elements.
   */
  loadStripeJs(): Observable<void> {
    return this._loadScript('https://js.stripe.com/v3/');
  }

  /**
   * Dynamically loads checkout.stripe.com/checkout.js (once) and emits when ready.
   * Call this from components that open the Stripe Checkout modal.
   */
  loadStripeCheckoutJs(): Observable<void> {
    return this._loadScript('https://checkout.stripe.com/checkout.js');
  }

  private _loadScript(src: string): Observable<void> {
    const win = this._document.defaultView as any;
    if (src.includes('js.stripe.com') && win?.Stripe) {
      return of(undefined);
    }
    if (src.includes('checkout.stripe.com') && win?.StripeCheckout) {
      return of(undefined);
    }
    const existing = this._document.querySelector(
      `script[src="${src}"]`
    ) as HTMLScriptElement | null;
    if (existing) {
      return new Observable<void>(observer => {
        existing.addEventListener(
          'load',
          () => {
            observer.next();
            observer.complete();
          },
          { once: true }
        );
        existing.addEventListener(
          'error',
          () => observer.error(new Error(`Failed to load ${src}`)),
          { once: true }
        );
      });
    }
    return new Observable<void>(observer => {
      const script = this._document.createElement('script') as HTMLScriptElement;
      script.src = src;
      script.type = 'text/javascript';
      script.addEventListener(
        'load',
        () => {
          observer.next();
          observer.complete();
        },
        { once: true }
      );
      script.addEventListener(
        'error',
        () => observer.error(new Error(`Failed to load ${src}`)),
        { once: true }
      );
      this._document.head.appendChild(script);
    });
  }

  createTransaction(transaction: Transaction): Observable<StripeSubscription> {
    return this._http
      .post('api/stripe/transaction', JSON.stringify(transaction), httpOptions)
      .pipe(map(t => new StripeSubscription(t)));
  }

  private fetchStripePublicApiKey(): Observable<string> {
    return this._http.get(`api/stripe/publickey`).pipe(map(res => res as string));
  }

  /**
   * Gets the stripe account for the logged in user
   */
  getStripeCustomer(): Observable<StripeCustomer> {
    return this._http.get<StripeCustomer>('api/stripe/customer');
  }

  /**
   * Send the stripe transaction to the server to update the customer credit card
   * @param trans
   */
  updateCreditCardStripeTransaction(trans: StripeTransaction) {
    return this._http.put(
      'api/stripe/update-card-transaction',
      JSON.stringify(trans),
      new JsonHeader()
    );
  }
}
